import React, { useState, useEffect } from "react";
import {
  FaTimes,
  FaSave,
  FaLayerGroup,
  FaPlus,
  FaImage,
  FaTrash,
  FaMinus,
} from "react-icons/fa";

type QuestionType =
  | "TRUE_FALSE"
  | "MULTIPLE_CHOICE"
  | "MULTIPLE_CHOICE_4"
  | "TEXT_INPUT"
  | "MATCHING"
  | "AUDIO_MULTIPLE_CHOICE"
  | "AUDIO_TRUE_FALSE"
  | "ESSAY"
  | "EMAIL"
  | "LETTER"
  | "OTHER";

interface QuestionFormData {
  module: string;
  questionType: QuestionType;
  questionText: string;
  contextText: string;
  options: string[];
  correctAnswer: string;
  points: number;
  examPartId?: number;
}

interface ExamPart {
  id: number;
  displayName: string;
  questionCount: number;
  totalPoints: number;
}

interface Props {
  isOpen: boolean;
  isEditing: boolean;
  formData: QuestionFormData;
  availableParts: ExamPart[];
  imageFile: File | null;
  editingQuestion?: any; // Question being edited (for getting questionNumber)
  onClose: () => void;
  onSave: (e: React.FormEvent) => void;
  onChange: (data: Partial<QuestionFormData>) => void;
  onImageChange: (file: File | null) => void;
  onCreatePart: () => void;
}

// Question type configurations by module
const QUESTION_TYPES_BY_MODULE: Record<
  string,
  { value: QuestionType; label: string; icon: string }[]
> = {
  LESEN: [
    { value: "MULTIPLE_CHOICE", label: "Multiple Choice (a/b/c)", icon: "○" },
    {
      value: "MULTIPLE_CHOICE_4",
      label: "Multiple Choice (a/b/c/d)",
      icon: "○",
    },
    { value: "MATCHING", label: "Matching (Zuordnung)", icon: "↔️" },
    { value: "TRUE_FALSE", label: "True/False (Richtig/Falsch)", icon: "✓" },
    { value: "TEXT_INPUT", label: "Text Input (Short Answer)", icon: "✍️" },
  ],
  HOREN: [
    {
      value: "AUDIO_MULTIPLE_CHOICE",
      label: "🎧 Audio + Multiple Choice",
      icon: "🎧",
    },
    { value: "AUDIO_TRUE_FALSE", label: "🎧 Audio + True/False", icon: "🎧" },
    { value: "MULTIPLE_CHOICE", label: "Multiple Choice (a/b/c)", icon: "○" },
    {
      value: "MULTIPLE_CHOICE_4",
      label: "Multiple Choice (a/b/c/d)",
      icon: "○",
    },
    { value: "MATCHING", label: "Matching (Zuordnung)", icon: "↔️" },
    { value: "TRUE_FALSE", label: "True/False (Ja/Nein)", icon: "✓" },
  ],
  SCHREIBEN: [
    { value: "EMAIL", label: "✉️ Email Writing", icon: "✉️" },
    { value: "LETTER", label: "📄 Letter Writing", icon: "📄" },
    { value: "ESSAY", label: "📝 Essay (Long Writing)", icon: "📝" },
    { value: "TEXT_INPUT", label: "Text Input (Short Answer)", icon: "✍️" },
  ],
};

// Available letter options for matching
const MATCHING_LETTERS = [
  "a",
  "b",
  "c",
  "d",
  "e",
  "f",
  "g",
  "h",
  "i",
  "j",
  "k",
  "l",
];

export const QuestionModal: React.FC<Props> = ({
  isOpen,
  isEditing,
  formData,
  availableParts,
  imageFile,
  editingQuestion,
  onClose,
  onSave,
  onChange,
  onImageChange,
  onCreatePart,
}) => {
  // State for matching pairs
  const [matchingPairs, setMatchingPairs] = useState<Record<number, string>>(
    {}
  );
  const [numberOfPairs, setNumberOfPairs] = useState(5);

  // Parse existing matching answer when editing
  useEffect(() => {
    if (
      isOpen &&
      formData.questionType === "MATCHING" &&
      formData.correctAnswer
    ) {
      const pairs: Record<number, string> = {};
      const pairStrings = formData.correctAnswer
        .split(",")
        .map((p) => p.trim());

      pairStrings.forEach((pairStr) => {
        const match = pairStr.match(/^(\d+)-([a-z])$/i);
        if (match) {
          pairs[parseInt(match[1])] = match[2].toLowerCase();
        }
      });

      if (Object.keys(pairs).length > 0) {
        setMatchingPairs(pairs);
        setNumberOfPairs(Object.keys(pairs).length);
      }
    }
  }, [isOpen, formData.questionType, formData.correctAnswer]);

  // Update correctAnswer when matching pairs change
  useEffect(() => {
    if (formData.questionType === "MATCHING") {
      const pairStrings = Object.entries(matchingPairs)
        .filter(([_, letter]) => letter) // Only include pairs with selected letters
        .sort(([a], [b]) => parseInt(a) - parseInt(b))
        .map(([num, letter]) => `${num}-${letter}`);

      onChange({ correctAnswer: pairStrings.join(", ") });
    }
  }, [matchingPairs, formData.questionType]);

  // Force clear options when question type doesn't need them
  useEffect(() => {
    const needsOptions = [
      "MULTIPLE_CHOICE",
      "MULTIPLE_CHOICE_4",
      "AUDIO_MULTIPLE_CHOICE",
    ].includes(formData.questionType);

    // If current type doesn't need options but options exist, clear them
    if (!needsOptions && formData.options.length > 0) {
      console.log("🧹 Clearing options for:", formData.questionType);
      onChange({ options: [] });
    }
  }, [formData.questionType]);

  if (!isOpen) return null;

  // Determine which fields to show based on question type
  const needsOptions = [
    "MULTIPLE_CHOICE",
    "MULTIPLE_CHOICE_4",
    "AUDIO_MULTIPLE_CHOICE",
  ].includes(formData.questionType);

  // Debug logging
  console.log("📋 QuestionModal Debug:", {
    questionType: formData.questionType,
    needsOptions,
    optionsLength: formData.options.length,
    options: formData.options,
  });

  const isMatching = formData.questionType === "MATCHING";

  const isWritingTask = ["ESSAY", "EMAIL", "LETTER"].includes(
    formData.questionType
  );

  const isTrueFalse = ["TRUE_FALSE", "AUDIO_TRUE_FALSE"].includes(
    formData.questionType
  );

  const needsCorrectAnswer = !["ESSAY", "EMAIL", "LETTER"].includes(
    formData.questionType
  );

  const defaultOptionsCount =
    formData.questionType === "MULTIPLE_CHOICE_4" ? 4 : 3;

  const types = QUESTION_TYPES_BY_MODULE[formData.module] || [];

  const handleMatchingPairChange = (pairNumber: number, letter: string) => {
    setMatchingPairs((prev) => ({
      ...prev,
      [pairNumber]: letter,
    }));
  };

  const handleNumberOfPairsChange = (newNumber: number) => {
    setNumberOfPairs(newNumber);

    // Remove pairs that exceed the new number
    const updatedPairs = { ...matchingPairs };
    Object.keys(updatedPairs).forEach((key) => {
      if (parseInt(key) > newNumber) {
        delete updatedPairs[parseInt(key)];
      }
    });
    setMatchingPairs(updatedPairs);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-xl font-bold">
            {isEditing ? "Edit" : "Add"} Question
          </h3>
          <button onClick={onClose}>
            <FaTimes size={24} className="text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <form onSubmit={onSave} className="p-6 space-y-6">
          {/* Module Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Module *
            </label>
            <select
              value={formData.module}
              onChange={(e) => {
                const newModule = e.target.value;
                const moduleTypes = QUESTION_TYPES_BY_MODULE[newModule] || [];
                const newQuestionType =
                  moduleTypes[0]?.value || "MULTIPLE_CHOICE";

                // Check if new question type needs options
                const needsOptionsForNewType = [
                  "MULTIPLE_CHOICE",
                  "MULTIPLE_CHOICE_4",
                  "AUDIO_MULTIPLE_CHOICE",
                ].includes(newQuestionType);

                onChange({
                  module: newModule,
                  questionType: newQuestionType,
                  options: needsOptionsForNewType ? ["", "", ""] : [], // Conditional options
                });
              }}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#009fe3]"
              required
            >
              <option value="LESEN">📖 Lesen (Reading)</option>
              <option value="HOREN">🎧 Hören (Listening)</option>
              <option value="SCHREIBEN">✍️ Schreiben (Writing)</option>
            </select>
          </div>

          {/* Teil Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <FaLayerGroup className="text-[#009fe3]" />
              Teil (Part) *
            </label>
            {availableParts.length === 0 ? (
              <div className="space-y-3">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 font-semibold">
                    ⚠️ No Teile for {formData.module} yet
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onCreatePart}
                  className="w-full px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <FaPlus />
                  Create {formData.module} Teil Now
                </button>
              </div>
            ) : (
              <select
                value={formData.examPartId || ""}
                onChange={(e) =>
                  onChange({ examPartId: parseInt(e.target.value) })
                }
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                required
              >
                <option value="">Select a Teil...</option>
                {availableParts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName} ({p.questionCount} questions,{" "}
                    {p.totalPoints} points)
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Question Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Question Type *
            </label>
            <select
              value={formData.questionType}
              onChange={(e) => {
                const newType = e.target.value as QuestionType;

                // Determine if new type needs options
                const needsOptionsForNewType = [
                  "MULTIPLE_CHOICE",
                  "MULTIPLE_CHOICE_4",
                  "AUDIO_MULTIPLE_CHOICE",
                ].includes(newType);

                // Set default options based on type
                let newDefaultOptions: string[] = [];
                if (needsOptionsForNewType) {
                  newDefaultOptions =
                    newType === "MULTIPLE_CHOICE_4"
                      ? ["", "", "", ""]
                      : ["", "", ""];
                }

                onChange({
                  questionType: newType,
                  options: newDefaultOptions,
                  correctAnswer: "", // Reset correct answer
                });

                // Reset matching pairs when switching to MATCHING
                if (newType === "MATCHING") {
                  setMatchingPairs({});
                  setNumberOfPairs(5);
                }
              }}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#009fe3]"
              required
            >
              {types.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.icon} {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Question Text */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Question Text *
            </label>
            <textarea
              value={formData.questionText}
              onChange={(e) => onChange({ questionText: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#009fe3]"
              placeholder={
                isWritingTask
                  ? "Enter task instructions (e.g., 'Schreiben Sie eine E-Mail an...')"
                  : isMatching
                  ? "e.g., 'Was haben Julias und Leons Freunde am Wochenende gemacht?'"
                  : "Enter the question text"
              }
              required
            />
          </div>

          {/* Question-Specific Context */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Question-Specific Context (Optional)
            </label>
            <textarea
              value={formData.contextText}
              onChange={(e) => onChange({ contextText: e.target.value })}
              rows={4}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#009fe3]"
              placeholder="Only if this question has different context than the Teil's shared context"
            />
            <p className="text-xs text-gray-500 mt-1">
              💡 Leave empty if Teil has shared context
            </p>
          </div>

          {/* Question Image */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <FaImage className="text-[#009fe3]" />
              Question Image (Optional)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => onImageChange(e.target.files?.[0] || null)}
              className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {imageFile && (
              <p className="text-sm text-green-600 mt-2">✅ {imageFile.name}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {isMatching
                ? "💡 For matching questions with images (e.g., Hören Teil 2)"
                : "💡 For diagrams or visual questions"}
            </p>
          </div>

          {/* MATCHING - Special Section */}
          {isMatching && (
            <div className="border-2 border-purple-200 rounded-xl p-5 bg-purple-50">
              <div className="mb-4">
                <label className="block text-sm font-semibold text-purple-900 mb-3">
                  ↔️ Matching Pairs Configuration
                </label>

                {/* Number of Pairs */}
                <div className="mb-4 p-4 bg-white rounded-lg border border-purple-200">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Number of Pairs *
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        handleNumberOfPairsChange(
                          Math.max(1, numberOfPairs - 1)
                        )
                      }
                      className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                      disabled={numberOfPairs <= 1}
                    >
                      <FaMinus />
                    </button>
                    <input
                      type="number"
                      value={numberOfPairs}
                      onChange={(e) =>
                        handleNumberOfPairsChange(
                          Math.max(1, parseInt(e.target.value) || 1)
                        )
                      }
                      className="w-20 px-3 py-2 border-2 border-purple-300 rounded-lg text-center font-bold text-lg"
                      min="1"
                      max="20"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        handleNumberOfPairsChange(
                          Math.min(20, numberOfPairs + 1)
                        )
                      }
                      className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                      disabled={numberOfPairs >= 20}
                    >
                      <FaPlus />
                    </button>
                    <span className="text-sm text-gray-600 ml-2">
                      (Questions to match)
                    </span>
                  </div>
                </div>

                {/* Calculate starting question number */}
                {(() => {
                  // Get current question number - will be used as starting number
                  const startingQuestionNumber = isEditing
                    ? editingQuestion?.questionNumber || 1
                    : (availableParts.find((p) => p.id === formData.examPartId)
                        ?.questionCount || 0) + 1;

                  return (
                    <>
                      <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="text-sm text-blue-800">
                          📊 <strong>Question Numbers:</strong>{" "}
                          {startingQuestionNumber} to{" "}
                          {startingQuestionNumber + numberOfPairs - 1}
                        </div>
                      </div>

                      {/* Pair Selection Dropdowns */}
                      <div className="space-y-2">
                        <div className="text-sm font-semibold text-gray-700 mb-3">
                          Select Correct Answer for Each Number:
                        </div>
                        {Array.from({ length: numberOfPairs }, (_, i) => {
                          const questionNum = startingQuestionNumber + i;
                          return (
                            <div
                              key={questionNum}
                              className="flex items-center gap-3 p-3 bg-white rounded-lg border border-purple-200"
                            >
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg flex items-center justify-center font-bold text-lg shrink-0">
                                {questionNum}
                              </div>
                              <span className="text-lg text-gray-600">→</span>
                              <select
                                value={matchingPairs[questionNum] || ""}
                                onChange={(e) =>
                                  handleMatchingPairChange(
                                    questionNum,
                                    e.target.value
                                  )
                                }
                                className="flex-1 px-4 py-2 border-2 border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-semibold text-purple-700"
                                required
                              >
                                <option value="">Select letter...</option>
                                {MATCHING_LETTERS.map((letter) => (
                                  <option key={letter} value={letter}>
                                    {letter.toUpperCase()}
                                  </option>
                                ))}
                              </select>
                            </div>
                          );
                        })}
                      </div>

                      {/* Answer Preview */}
                      {Object.keys(matchingPairs).length > 0 && (
                        <div className="mt-4 p-4 bg-green-50 border-2 border-green-300 rounded-lg">
                          <div className="text-sm font-semibold text-green-800 mb-2">
                            ✨ Answer Preview:
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(matchingPairs)
                              .filter(([_, letter]) => letter)
                              .sort(([a], [b]) => parseInt(a) - parseInt(b))
                              .map(([num, letter]) => (
                                <span
                                  key={num}
                                  className="px-3 py-1 bg-white border-2 border-green-500 rounded-lg text-sm font-bold text-green-700"
                                >
                                  {num} → {letter.toUpperCase()}
                                </span>
                              ))}
                          </div>
                          <div className="mt-2 text-xs text-green-700 font-mono">
                            {formData.correctAnswer}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                💡 <strong>How it works:</strong> Students will see the image
                with all items and select the correct letter for each question
                number from a dropdown
              </div>
            </div>
          )}

          {/* Answer Options - Multiple Choice ONLY (not for MATCHING) */}
          {needsOptions && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Answer Options *
              </label>
              <div className="space-y-2">
                {formData.options.map((opt, i) => (
                  <div key={i} className="flex gap-2">
                    <span className="w-8 h-8 bg-gray-200 text-gray-700 font-bold rounded inline-flex items-center justify-center">
                      {String.fromCharCode(97 + i)}
                    </span>
                    <input
                      type="text"
                      value={opt}
                      onChange={(e) => {
                        const newOpts = [...formData.options];
                        newOpts[i] = e.target.value;
                        onChange({ options: newOpts });
                      }}
                      className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                      placeholder={`Option ${String.fromCharCode(97 + i)}`}
                      required
                    />
                    {formData.options.length > defaultOptionsCount && (
                      <button
                        type="button"
                        onClick={() => {
                          const newOpts = formData.options.filter(
                            (_, idx) => idx !== i
                          );
                          onChange({ options: newOpts });
                        }}
                        className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                      >
                        <FaTrash size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Correct Answer - For non-MATCHING questions */}
          {needsCorrectAnswer && !isMatching && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Correct Answer *
              </label>

              {/* True/False Dropdown */}
              {isTrueFalse && (
                <select
                  value={formData.correctAnswer}
                  onChange={(e) => onChange({ correctAnswer: e.target.value })}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                  required
                >
                  <option value="">Select correct answer...</option>
                  <option value="Richtig">✓ Richtig (True)</option>
                  <option value="Falsch">✗ Falsch (False)</option>
                </select>
              )}

              {/* Multiple Choice Dropdown */}
              {needsOptions && !isTrueFalse && (
                <select
                  value={formData.correctAnswer}
                  onChange={(e) => onChange({ correctAnswer: e.target.value })}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                  required
                >
                  <option value="">Select correct option...</option>
                  {formData.options.map((opt, i) => (
                    <option key={i} value={opt}>
                      {String.fromCharCode(97 + i)}: {opt || "(empty)"}
                    </option>
                  ))}
                </select>
              )}

              {/* Text Input */}
              {!needsOptions &&
                !isTrueFalse &&
                formData.questionType === "TEXT_INPUT" && (
                  <input
                    type="text"
                    value={formData.correctAnswer}
                    onChange={(e) =>
                      onChange({ correctAnswer: e.target.value })
                    }
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                    placeholder="Enter the correct answer"
                    required
                  />
                )}

              {/* Other Types */}
              {!needsOptions &&
                !isTrueFalse &&
                formData.questionType === "OTHER" && (
                  <input
                    type="text"
                    value={formData.correctAnswer}
                    onChange={(e) =>
                      onChange({ correctAnswer: e.target.value })
                    }
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                    placeholder="Enter the correct answer (if applicable)"
                  />
                )}
            </div>
          )}

          {/* Writing Task Notice */}
          {isWritingTask && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>ℹ️ Writing Task:</strong> This question will be manually
                graded by mentors. No automatic grading available.
              </p>
            </div>
          )}

          {/* Points */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Points *
            </label>
            <input
              type="number"
              value={formData.points}
              onChange={(e) =>
                onChange({ points: parseInt(e.target.value) || 0 })
              }
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#009fe3]"
              min="1"
              max="100"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              💡 Typical: MCQ = 1 pt, Matching = {numberOfPairs} pts (1 per
              pair), Writing = 10-30 pts
            </p>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="submit"
              disabled={
                !formData.examPartId ||
                (isMatching && Object.keys(matchingPairs).length === 0)
              }
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#009fe3] text-white rounded-lg font-semibold hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaSave />
              {isEditing ? "Update" : "Add"} Question
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
